import type { Pagination } from 'models';
import { generateQueryKey } from 'query';
import type { ChannelsEventsListFilter, EventsInviteesFilter, EventsListFilter } from '../types';
import type { EventParticipantsFilter } from './use-event-participants';
import type { NodeEventsListFilter } from './useNodeEventsInfiniteQuery';

type EventParticipantsParams = {
  filter?: EventParticipantsFilter;
  pagination?: Partial<Pagination>;
};

type EventsInviteesParams = {
  filter?: EventsInviteesFilter;
  pagination?: Partial<Pagination>;
};

type EventsParams = {
  filter?: ChannelsEventsListFilter | EventsListFilter;
  pagination?: Partial<Pagination>;
};

type NodeEventsParams = {
  filter?: Partial<NodeEventsListFilter>;
  pagination?: Partial<Pagination>;
};

const featureKey = 'events';

const nodeEventsKey = 'node';

export const getEventsFeatureQueryKey = () => generateQueryKey([featureKey]);

export const getNodeEventsFeatureQueryKey = () => generateQueryKey([nodeEventsKey]);

export const getEventDetailFeatureQueryKey = () => generateQueryKey([...getEventsFeatureQueryKey(), 'detail']);

export const getNodeEventDetailFeatureQueryKey = () => generateQueryKey([...getNodeEventsFeatureQueryKey(), 'detail']);

export const getEventDetailQueryKey = ({
  id,
  ...params
}: {
  id?: string;
  invitationToken?: string;
  view?: 'internal' | 'public-deprecated' | 'public';
}) => generateQueryKey([...getEventDetailFeatureQueryKey(), id, params]);

export const getProgramEventLayoutQueryKey = ({ id }: { id?: string }) =>
  generateQueryKey([...getEventDetailFeatureQueryKey(), id, 'layout']);

export const getNodeEventDetailQueryKey = ({ eventId, skipAuth }: { eventId: string; skipAuth?: boolean }) =>
  generateQueryKey([...getNodeEventDetailFeatureQueryKey(), eventId, { skipAuth }]);

export const getNodeEventEnterQueryKey = ({ eventId, invitationToken }: { eventId: string; invitationToken: string }) =>
  generateQueryKey([...getNodeEventDetailFeatureQueryKey(), eventId, 'enter', { invitationToken }]);

export const getInvitationTokenKey = (params: { invitationToken?: string }) =>
  generateQueryKey([...getEventsFeatureQueryKey(), 'invitation-token', params]);

export const getEventsQueryKey = ({ filter = {}, pagination = {} }: EventsParams) =>
  generateQueryKey([...getEventsFeatureQueryKey(), 'list', { filter, pagination }]);

export const getNodeEventsQueryKey = ({ filter = {}, pagination = {} }: NodeEventsParams) =>
  generateQueryKey([...getNodeEventsFeatureQueryKey(), 'list', { filter, pagination }]);

export const getEventsInfiniteQueryKey = ({ filter = {}, pagination = {} }: EventsParams) =>
  generateQueryKey([...getEventsQueryKey({ filter, pagination }), 'infinite']);

export const getEventsInviteesInfiniteQueryKey = ({ filter, pagination = {} }: EventsInviteesParams) =>
  generateQueryKey([...getEventsFeatureQueryKey(), 'invitees', { filter, pagination }]);

export const getEventParticipantsInfiniteQueryKey = ({ filter, pagination = {} }: EventParticipantsParams) =>
  generateQueryKey([...getEventsFeatureQueryKey(), 'participants', { filter, pagination }]);

export const getNodeEventsInfiniteQueryKey = ({ filter, pagination }: NodeEventsParams) =>
  generateQueryKey([...getNodeEventsQueryKey({ filter, pagination }), 'infinite']);
